import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ProfileHeader from '../components/ProfileHeader';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import ParticipantsSection from '../components/client/ParticipantsSection';
import { useSelectedOrganizationContext } from '../utils/organizationSelection';
import {
  buildClientProgressSummary,
  buildClientWorkspace,
  buildFollowUpDraftPrompt,
  buildPackageOfferCoachingSummary,
  buildPackageOfferDraftPrompt,
  buildRebookCoachingSummary,
  buildRebookSuggestionExplanation,
  createClientNote,
  createRebookRequestFromLastService,
  getClientProfilesForUser,
  getClientInactivitySignals,
  scoreClientRetentionRisk,
  sendRebookPromptNotification,
  suggestNextServiceForClient,
  updateClientProfile,
} from '../utils/clientCRM';
import {
  getClientRetentionActions,
  getRetentionActionSummary,
} from '../utils/retentionActions';
import { getOrganizationServices } from '../utils/services';
import {
  canAddClientNote,
  canEditClientProfile,
  canViewClientProfile,
  canViewClientRecommendations,
  canViewClientRevenue,
  canViewClientTimeline,
  canUseAIAssistDrafts,
} from '../utils/platformAccess';
import AiInsightPanel from '../components/ai/AiInsightPanel';
import QuickCoachNotesBox from '../components/ai/copilot/QuickCoachNotesBox';
import { buildClientRetentionOpportunity } from '../utils/retentionOpportunity';

function formatDate(value) {
  const date = (() => {
    if (!value) return null;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  })();

  return date ? date.toLocaleString() : 'Not yet';
}

function ClientProfilePage() {
  const params = useParams() || {};
  // Pass 79 — TWO addressing modes, kept strictly separate:
  //  · CANONICAL clientId route `/org/:organizationId/clients/:clientId` — a COMMERCIAL identity (may have NO Auth).
  //  · LEGACY Auth route `/clients/:clientUserId` — an Auth uid, NEVER reinterpreted as a clientId.
  // The legacy `?clientId=` query is still honoured for older links.
  const clientUserId = params.clientUserId || '';
  const [searchParams] = useSearchParams();
  const clientId = params.clientId || searchParams.get('clientId') || '';
  const routeOrganizationId = params.organizationId || '';
  const { currentUser, userData } = useAuth() || {};
  const { selectedOrganizationId, selectedOrganization } = useSelectedOrganizationContext() || {};
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [services, setServices] = useState([]);
  const [retentionActions, setRetentionActions] = useState([]);
  const [aiCoachContext, setAiCoachContext] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [noteForm, setNoteForm] = useState({
    noteText: '',
    tags: '',
    visibility: 'org',
  });
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    primaryDiscipline: '',
    skillLevel: '',
    goals: '',
    tags: '',
    notesInternal: '',
  });

  useEffect(() => {
    let active = true;

    const load = async () => {
      // Pass 79 — a client is addressable by the canonical clientId OR a legacy Auth uid.
      if (!clientUserId && !clientId) {
        setWorkspace(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        // org from the canonical route param first, then the selected org, then (self-view) the user's own profile.
        let organizationId = routeOrganizationId || selectedOrganizationId || '';

        if (!organizationId && clientUserId && currentUser?.uid === clientUserId) {
          const profiles = await getClientProfilesForUser(db, clientUserId);
          organizationId = profiles[0]?.organizationId || '';
        }

        if (!organizationId) {
          if (active) {
            setWorkspace(null);
            setLoading(false);
          }
          return;
        }

        const nextServices = await getOrganizationServices(db, organizationId);
        // READ-ONLY (§21/§44) — no persistProfile; opening a client never writes.
        const nextWorkspace = await buildClientWorkspace(db, {
          organizationId,
          clientUserId,
          clientId,
          services: nextServices,
        });
        // §28 — retention actions are clientId-native: a no-Auth client (clientId, no Auth uid) still has history.
        const commercialClientId = clientId
          || (nextWorkspace?.bookings || []).map((b) => b.clientId).find(Boolean)
          || (nextWorkspace?.lessons || []).map((l) => l.clientId).find(Boolean)
          || '';
        const nextRetentionActions = (clientUserId || commercialClientId)
          ? await getClientRetentionActions(db, { organizationId, clientId: commercialClientId, clientUserId })
          : [];

        if (!active) return;
        setWorkspace(nextWorkspace);
        setServices(nextServices || []);
        setRetentionActions(nextRetentionActions || []);
        setProfileForm({
          displayName: nextWorkspace?.clientProfile?.displayName || '',
          primaryDiscipline: nextWorkspace?.clientProfile?.primaryDiscipline || '',
          skillLevel: nextWorkspace?.clientProfile?.skillLevel || '',
          goals: nextWorkspace?.clientProfile?.goals || '',
          tags: (nextWorkspace?.clientProfile?.tags || []).join(', '),
          notesInternal: nextWorkspace?.clientProfile?.notesInternal || '',
        });
      } catch (loadError) {
        if (!active) return;
        setError(loadError?.message || 'Could not load the client workspace right now.');
        setWorkspace(null);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [clientUserId, clientId, routeOrganizationId, currentUser?.uid, selectedOrganizationId]);

  const organizationId = workspace?.clientProfile?.organizationId || routeOrganizationId || selectedOrganizationId || '';
  // The canonical commercial identity to write against — the route/query clientId, else the explicit clientId already
  // carried by this client's own canonical records (never minted from an Auth id).
  const resolvedClientId = clientId
    || (workspace?.bookings || []).map((b) => b.clientId).find(Boolean)
    || (workspace?.lessons || []).map((l) => l.clientId).find(Boolean)
    || '';
  const coachingMemory = workspace?.coachingMemory || null;
  const hasAuthAccount = Boolean(workspace?.hasAuthAccount);
  const canViewProfile = canViewClientProfile(userData || {}, {
    currentUserId: currentUser?.uid || '',
    organizationId,
    clientUserId,
  });
  const canViewTimeline = canViewClientTimeline(userData || {}, {
    currentUserId: currentUser?.uid || '',
    organizationId,
    clientUserId,
  });
  const canAddNotes = canAddClientNote(userData || {}, { organizationId });
  const canEditProfile = canEditClientProfile(userData || {}, { organizationId });
  const canViewRevenue = canViewClientRevenue(userData || {}, { organizationId });
  const canViewRecommendations = canViewClientRecommendations(userData || {}, {
    currentUserId: currentUser?.uid || '',
    organizationId,
    clientUserId,
  });
  const canUseAI = canUseAIAssistDrafts(userData || {}, organizationId);

  const suggestedService = useMemo(
    () => suggestNextServiceForClient(workspace?.clientProfile || {}, {
      bookings: workspace?.bookings || [],
      services,
    }),
    [workspace, services]
  );

  const latestMessageThread = useMemo(
    () => (workspace?.timeline || []).find((entry) => entry.type === 'message'),
    [workspace]
  );

  const retentionSignals = useMemo(
    () => getClientInactivitySignals(workspace?.clientProfile || {}, {
      bookings: workspace?.bookings || [],
      lessons: workspace?.lessons || [],
      feedbackHistory: workspace?.feedbackHistory || [],
      timeline: workspace?.timeline || [],
      memberships: workspace?.packageSummary?.activeMemberships || [],
    }),
    [workspace]
  );

  const retentionRisk = useMemo(
    () => scoreClientRetentionRisk(retentionSignals) || { level: 'low', reasons: [] },
    [retentionSignals]
  );

  // Pass 80 §60 — the SAME canonical retention contract that powers the org queue, scoped to this one client. It is
  // delivery-truthful, clientId-native, and works for a no-Auth commercial client (no fabricated package/message).
  const retentionOpportunity = useMemo(
    () => buildClientRetentionOpportunity({
      organizationId,
      clientId: resolvedClientId,
      clientUserId,
      displayName: workspace?.clientProfile?.displayName || '',
      bookings: workspace?.bookings || [],
      bookingRequests: workspace?.bookingRequests || [],
      lessons: workspace?.lessons || [],
      memberships: workspace?.packageSummary?.activeMemberships || [],
      services,
      retentionActions,
      coachingMemory,
      hasAuthAccount,
      now: Date.now(),
    }),
    [organizationId, resolvedClientId, clientUserId, workspace, services, retentionActions, coachingMemory, hasAuthAccount]
  );
  const retentionActionSummary = useMemo(
    () => getRetentionActionSummary(retentionActions) || {
      totalCount: 0,
      byType: {},
      followUpSentCount: 0,
      packageOfferedCount: 0,
      rebookSuggestedCount: 0,
      snoozedCount: 0,
      reviewedCount: 0,
    },
    [retentionActions]
  );

  // §44 read-only refresh — re-read the workspace WITHOUT any profile persistence.
  const refreshWorkspace = async () => {
    const refreshed = await buildClientWorkspace(db, { organizationId, clientUserId, clientId, services });
    setWorkspace(refreshed);
  };

  const handleCreateNote = async () => {
    // §16/§17 a manual note is clientId-native; it needs the canonical clientId + the authoring staff's Auth uid.
    if (!resolvedClientId || !noteForm.noteText.trim()) return;

    try {
      await createClientNote(db, {
        organizationId,
        clientId: resolvedClientId,
        clientUserId: workspace?.clientProfile?.userId || '', // OPTIONAL explicit Auth link only
        createdByAuthUserId: currentUser?.uid || '',
        createdByNameSnapshot: userData?.name || currentUser?.email || '',
        noteText: noteForm.noteText,
        tags: noteForm.tags,
        visibility: noteForm.visibility,
      });
      setMessage('Client note saved.');
      setNoteForm({ noteText: '', tags: '', visibility: 'org' });
      await refreshWorkspace();
    } catch (saveError) {
      setError(saveError?.message || 'Could not save the client note.');
    }
  };

  const handleCreateRebook = async () => {
    if (!workspace?.clientProfile) return;

    try {
      // §38 — pass the canonical clientId explicitly; a no-Auth rebook creates a bookingRequest with NO requesterUserId
      // fabrication (createRebookRequestFromLastService preserves clientId and never mints an Auth id).
      await createRebookRequestFromLastService(db, {
        organizationId,
        clientProfile: workspace.clientProfile,
        clientId: resolvedClientId,
        bookings: workspace.bookings || [],
        services,
        actorUserId: currentUser?.uid || '',
        actorName: userData?.name || currentUser?.email || '',
      });
      setMessage('Rebook request created from the client CRM.');
      await refreshWorkspace();
    } catch (rebookError) {
      setError(rebookError?.message || 'Could not create a rebook request.');
    }
  };

  const handleSaveProfile = async () => {
    if (!workspace?.clientProfile) return;

    try {
      const updatedProfile = await updateClientProfile(db, {
        organizationId,
        clientId: resolvedClientId, // canonical identity (immutable); userId is the optional Auth link
        userId: workspace.clientProfile.userId || '',
        updates: {
          displayName: profileForm.displayName,
          primaryDiscipline: profileForm.primaryDiscipline,
          skillLevel: profileForm.skillLevel,
          goals: profileForm.goals,
          tags: profileForm.tags,
          notesInternal: profileForm.notesInternal,
        },
      });

      setWorkspace((current) => current ? {
        ...current,
        clientProfile: updatedProfile,
      } : current);
      setMessage('Client profile updated.');
    } catch (saveError) {
      setError(saveError?.message || 'Could not update the client summary.');
    }
  };

  const handleSendRebookPrompt = async () => {
    if (!workspace?.clientProfile) return;

    try {
      await sendRebookPromptNotification(db, {
        clientProfile: workspace.clientProfile,
        booking: workspace.bookings?.[0] || {},
        actorUserId: currentUser?.uid || '',
        actorName: userData?.name || currentUser?.email || '',
      });
      setMessage('Rebook prompt sent.');
    } catch (promptError) {
      setError(promptError?.message || 'Could not send the rebook prompt.');
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <ProfileHeader />
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-500">Loading client workspace...</p>
        </div>
      </div>
    );
  }

  if (!workspace || !canViewProfile) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <ProfileHeader />
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-950">Client CRM</h1>
          <p className="mt-3 text-sm text-slate-600">
            This client workspace is not available in your current organization context.
          </p>
        </div>
      </div>
    );
  }

  const { clientProfile, bookings, bookingRequests, lessons, videos, feedbackHistory, packageSummary, revenueSummary, notes, timeline, lastActivity } = workspace;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <ProfileHeader />
      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Client CRM</p>
            <h1 className="mt-2 text-3xl font-semibold text-slate-950">{clientProfile.displayName}</h1>
            <p className="mt-2 text-sm text-slate-600">
              {clientProfile.email || 'No email on file'} {clientProfile.primaryDiscipline ? `· ${clientProfile.primaryDiscipline}` : ''}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Last activity: {formatDate(lastActivity?.at)} {selectedOrganization?.organizationName ? `· ${selectedOrganization.organizationName}` : ''}
            </p>
            {/* §26/§50 — commercial identity state; a no-Auth client is a real commercial customer, not a missing one. */}
            <p className="mt-1 text-xs font-medium text-slate-400">
              {hasAuthAccount ? 'Linked app account' : 'Commercial client · No linked app account'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {/* §37 — a direct app thread needs an explicit linked account. No account → neutral unavailable state. */}
            {hasAuthAccount && latestMessageThread?.link ? (
              <Link
                to={latestMessageThread.link}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Message Client
              </Link>
            ) : (
              <span
                className="rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-slate-400"
                title={hasAuthAccount ? 'No message thread yet' : 'No linked app account'}
              >
                {hasAuthAccount ? 'No message thread yet' : 'Message · No linked app account'}
              </span>
            )}
            <button
              type="button"
              onClick={handleCreateRebook}
              className="rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Quick Rebook
            </button>
            <Link
              to="/organization"
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Back to Workspace
            </Link>
          </div>
        </div>

        {message ? (
          <p className="mt-4 rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p>
        ) : null}
        {error ? (
          <p className="mt-4 rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</p>
        ) : null}

        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-600">Bookings</p>
            <p className="mt-2 text-3xl font-bold text-slate-950">{clientProfile.totalBookings}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-600">Lessons</p>
            <p className="mt-2 text-3xl font-bold text-slate-950">{clientProfile.totalLessons}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-600">Videos</p>
            <p className="mt-2 text-3xl font-bold text-slate-950">{clientProfile.totalVideos}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-600">Revenue</p>
            <p className="mt-2 text-3xl font-bold text-slate-950">
              {canViewRevenue ? `${revenueSummary.totalRevenue} USD` : 'Private'}
            </p>
          </div>
        </div>
      </section>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.4fr,0.9fr]">
        <section className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Profile Summary</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                <p><span className="font-medium text-slate-900">Phone:</span> {clientProfile.phone || 'Not set'}</p>
                <p className="mt-2"><span className="font-medium text-slate-900">Country:</span> {clientProfile.country || 'Not set'}</p>
                <p className="mt-2"><span className="font-medium text-slate-900">Skill Level:</span> {clientProfile.skillLevel || 'Not set'}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                <p className="font-medium text-slate-900">Goals</p>
                <p className="mt-2">{clientProfile.goals || 'No goals recorded yet.'}</p>
                <p className="mt-3 text-xs text-slate-500">
                  Suggested next service: {suggestedService?.name || 'No clear recommendation yet'}
                </p>
              </div>
            </div>
            {canEditProfile ? (
              <div className="mt-4 rounded-2xl border border-slate-200 p-4">
                <p className="text-sm font-medium text-slate-900">Edit client summary</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <input className="rounded-xl border border-slate-300 px-3 py-2 text-sm" value={profileForm.displayName} onChange={(event) => setProfileForm((current) => ({ ...current, displayName: event.target.value }))} placeholder="Display name" />
                  <input className="rounded-xl border border-slate-300 px-3 py-2 text-sm" value={profileForm.primaryDiscipline} onChange={(event) => setProfileForm((current) => ({ ...current, primaryDiscipline: event.target.value }))} placeholder="Primary discipline" />
                  <input className="rounded-xl border border-slate-300 px-3 py-2 text-sm" value={profileForm.skillLevel} onChange={(event) => setProfileForm((current) => ({ ...current, skillLevel: event.target.value }))} placeholder="Skill level" />
                  <input className="rounded-xl border border-slate-300 px-3 py-2 text-sm" value={profileForm.tags} onChange={(event) => setProfileForm((current) => ({ ...current, tags: event.target.value }))} placeholder="Tags" />
                  <textarea className="rounded-xl border border-slate-300 px-3 py-2 text-sm md:col-span-2" rows={3} value={profileForm.goals} onChange={(event) => setProfileForm((current) => ({ ...current, goals: event.target.value }))} placeholder="Goals" />
                  <textarea className="rounded-xl border border-slate-300 px-3 py-2 text-sm md:col-span-2" rows={3} value={profileForm.notesInternal} onChange={(event) => setProfileForm((current) => ({ ...current, notesInternal: event.target.value }))} placeholder="Internal profile summary" />
                </div>
                <button type="button" onClick={handleSaveProfile} className="mt-3 rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
                  Save Summary
                </button>
              </div>
            ) : null}
          </div>

          {/* §4/§7/§15 — LONGITUDINAL COACHING MEMORY: one card per EXPLICIT participant, sport-separated, with a
              next-instructor brief. A family (one clientId) shows multiple independent participants — never merged. */}
          {canViewTimeline && coachingMemory ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Coaching Memory</h2>
              <p className="mt-1 text-sm text-slate-500">
                Derived from delivered lessons and reviewed coaching evidence. Each participant is tracked separately per sport.
              </p>
              {coachingMemory.participants.length === 0 && coachingMemory.unlinkedCoachingEvents.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">No delivered coaching evidence yet for this client.</p>
              ) : (
                <div className="mt-4 space-y-4">
                  {coachingMemory.participants.map((participant) => (
                    <div key={participant.participantId} className="rounded-2xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-900">{participant.displayLabel || 'Participant'}</p>
                        <span className="text-xs text-slate-400">{participant.lessonCount} lesson{participant.lessonCount === 1 ? '' : 's'}</span>
                      </div>
                      <div className="mt-3 grid gap-3 md:grid-cols-2">
                        {participant.sportMemories.map((sportMemory) => {
                          const brief = sportMemory.nextInstructorBrief;
                          return (
                            <div key={sportMemory.sport} className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{sportMemory.sport}</p>
                              {sportMemory.currentFocus ? (
                                <p className="mt-2"><span className="font-medium text-slate-900">Working on:</span> {sportMemory.currentFocus}</p>
                              ) : null}
                              {brief.demonstratedLevel ? (
                                <p className="mt-1"><span className="font-medium text-slate-900">Demonstrated level:</span> {brief.demonstratedLevel.replace(/_/g, ' ')}</p>
                              ) : null}
                              {sportMemory.demonstratedProgress.length > 0 ? (
                                <p className="mt-1 text-emerald-700">Improved: {sportMemory.demonstratedProgress[0].from.replace(/_/g, ' ')} → {sportMemory.demonstratedProgress[0].to.replace(/_/g, ' ')}</p>
                              ) : null}
                              {sportMemory.recurringPatterns.length > 0 ? (
                                <p className="mt-1"><span className="font-medium text-slate-900">Recurring:</span> {sportMemory.recurringPatterns[0].label.replace(/_/g, ' ')} (×{sportMemory.recurringPatterns[0].occurrences})</p>
                              ) : null}
                              {brief.lastInstructor ? (
                                <p className="mt-1 text-xs text-slate-500">Last instructor: {brief.lastInstructor}</p>
                              ) : null}
                              {brief.lastDrill ? (
                                <p className="mt-1 text-xs text-slate-500">Last drill: {brief.lastDrill}</p>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {coachingMemory.unlinkedCoachingEvents.length > 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-600">
                      <p className="font-medium text-slate-900">Unlinked coaching events ({coachingMemory.unlinkedCoachingEvents.length})</p>
                      <p className="mt-1 text-xs text-slate-500">Delivered lessons with no linked participant identity — not merged into any athlete's memory.</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}

          {/* Pass 85 — canonical PARTICIPANTS for this commercial client (the coached humans; distinct from the client). */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <ParticipantsSection organizationId={organizationId} clientId={resolvedClientId} bookings={workspace?.bookings || []} />
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-950">Upcoming and Recent Bookings</h2>
              <button
                type="button"
                onClick={handleSendRebookPrompt}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Send Rebook Prompt
              </button>
            </div>
            {bookings.length === 0 && bookingRequests.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">No booking history yet.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {bookings.slice(0, 5).map((booking) => (
                  <div key={booking.bookingId || booking.id} className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-900">{booking.serviceNameSnapshot || booking.serviceType || 'Booked session'}</p>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600">{booking.status}</span>
                    </div>
                    <p className="mt-2">{formatDate(booking.startAt || booking.updatedAt)}</p>
                  </div>
                ))}
                {bookingRequests.slice(0, 3).map((bookingRequest) => (
                  <div key={bookingRequest.bookingRequestId || bookingRequest.id} className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-700">
                    <p className="font-medium text-slate-900">{bookingRequest.serviceNameSnapshot || bookingRequest.serviceType || 'Booking request'}</p>
                    <p className="mt-2">{bookingRequest.status} · {formatDate(bookingRequest.requestedStartAt || bookingRequest.createdAt)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Lessons, Videos, and Feedback</h2>
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-900">Lessons</p>
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  {lessons.slice(0, 4).map((lesson) => (
                    <div key={lesson.id || lesson.lessonId}>
                      <p className="font-medium text-slate-900">{lesson.title || lesson.lessonTitle || 'Lesson'}</p>
                      <p>{formatDate(lesson.updatedAt || lesson.date || lesson.createdAt)}</p>
                    </div>
                  ))}
                  {lessons.length === 0 ? <p className="text-slate-500">No lessons yet.</p> : null}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-900">Videos</p>
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  {videos.slice(0, 4).map((video) => (
                    <div key={video.id}>
                      <p className="font-medium text-slate-900">{video.title || video.fileName || 'Video upload'}</p>
                      <p>{formatDate(video.updatedAt || video.createdAt)}</p>
                    </div>
                  ))}
                  {videos.length === 0 ? <p className="text-slate-500">No videos yet.</p> : null}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-900">Feedback and AI</p>
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  {feedbackHistory.slice(0, 4).map((feedbackItem) => (
                    <div key={feedbackItem.videoId}>
                      <p className="font-medium text-slate-900">{feedbackItem.title}</p>
                      <p>{feedbackItem.hasAnalysis ? 'Movement analysis available' : 'Coach feedback available'}</p>
                    </div>
                  ))}
                  {feedbackHistory.length === 0 ? <p className="text-slate-500">No feedback history yet.</p> : null}
                </div>
              </div>
            </div>
          </div>

          {canViewTimeline ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Activity Timeline</h2>
              {timeline.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">No client activity has been recorded yet.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {timeline.slice(0, 12).map((entry) => (
                    <div key={entry.id} className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium text-slate-900">{entry.title}</p>
                        <span className="text-xs uppercase tracking-wide text-slate-400">{entry.type}</span>
                      </div>
                      <p className="mt-2">{entry.summary}</p>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <p className="text-xs text-slate-500">{formatDate(entry.at)}</p>
                        {entry.link ? (
                          <Link className="text-xs font-medium text-blue-600 hover:underline" to={entry.link}>
                            Open
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {canViewRecommendations ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Next-Step Recommendations</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">Suggested next service</p>
                  <p className="mt-2">{workspace.recommendations?.nextService?.name || suggestedService?.name || 'No clear recommendation yet'}</p>
                  <p className="mt-3 text-xs text-slate-500">{workspace.recommendations?.rebookTiming?.message || 'Rebook timing not available yet.'}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">Suggested coaching focus</p>
                  <p className="mt-2">{workspace.recommendations?.coachingFocus || 'No coaching focus suggestion yet.'}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">Package opportunity</p>
                  <p className="mt-2">{workspace.recommendations?.packageSuggestion?.name || 'No package suggestion right now.'}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">Follow-up message</p>
                  <p className="mt-2">{workspace.recommendations?.followUpMessage || 'No follow-up message suggestion yet.'}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">Retention Risk</p>
                  <p className="mt-2 capitalize">{retentionRisk.level || 'low'} attention level</p>
                  <p className="mt-3 text-xs text-slate-500">
                    {buildRebookSuggestionExplanation(workspace.clientProfile, {
                      recommendations: workspace.recommendations,
                      risk: retentionRisk,
                    })}
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700 md:col-span-2">
                  <p className="font-medium text-slate-900">Explicit AI-Assist Hooks</p>
                  <div className="mt-3 space-y-3">
                    <p>{buildFollowUpDraftPrompt(workspace.clientProfile, {
                      recommendations: workspace.recommendations,
                      risk: retentionRisk,
                    })}</p>
                    <p>{buildPackageOfferDraftPrompt(workspace.clientProfile, {
                      recommendations: workspace.recommendations,
                    })}</p>
                    <p>{buildClientProgressSummary(workspace)}</p>
                    <p>{buildRebookCoachingSummary(workspace)}</p>
                    <p>{buildPackageOfferCoachingSummary(workspace)}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {canUseAI ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">AI Insights</h2>
              <p className="mt-1 text-sm text-slate-500">
                Generate AI-assisted summaries from this client's coaching history. Results are suggestions only — review before acting.
              </p>
              <div className="mt-4">
                <QuickCoachNotesBox
                  value={aiCoachContext}
                  onChange={setAiCoachContext}
                  label="Add context for AI (optional)"
                  placeholder="Quick notes — focus areas, recent observations, anything to personalise the AI insights…"
                />
              </div>
              <div className="mt-4">
                <AiInsightPanel
                  workspace={workspace}
                  orgId={organizationId}
                  services={services}
                  coachContext={aiCoachContext}
                />
              </div>
            </div>
          ) : null}
        </section>

        <section className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Packages and Memberships</h2>
            {!hasAuthAccount ? (
              /* §50 — package membership is keyed to an Auth account; for a no-Auth client it is UNAVAILABLE, not 0. */
              <p className="mt-4 rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                Package and membership balances are not available for a client without a linked app account.
              </p>
            ) : (
            <>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-600">Remaining Sessions</p>
                <p className="mt-2 text-3xl font-bold text-slate-950">{packageSummary.remainingSessions}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-600">Remaining Credits</p>
                <p className="mt-2 text-3xl font-bold text-slate-950">{packageSummary.remainingCredits}</p>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {packageSummary.activeMemberships.slice(0, 5).map((membership) => (
                <div key={membership.customerMembershipId || membership.id} className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">{membership.packageId || 'Membership'}</p>
                  <p className="mt-1">Sessions left: {membership.remainingSessions}</p>
                  <p className="mt-1">Credits left: {membership.remainingCredits}</p>
                </div>
              ))}
              {packageSummary.activeMemberships.length === 0 ? (
                <p className="text-sm text-slate-500">No active packages or memberships.</p>
              ) : null}
            </div>
            </>
            )}
          </div>

          {canAddNotes ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Instructor Notes</h2>
              <div className="mt-4 space-y-3">
                {notes.slice(0, 6).map((note) => (
                  <div key={note.clientNoteId || note.id} className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                    <p className="font-medium text-slate-900">{note.visibility === 'instructor-private' ? 'Instructor-private note' : 'Org note'}</p>
                    <p className="mt-2">{note.noteText}</p>
                    <p className="mt-2 text-xs text-slate-500">{formatDate(note.updatedAt || note.createdAt)}</p>
                  </div>
                ))}
                {notes.length === 0 ? <p className="text-sm text-slate-500">No internal notes yet.</p> : null}
              </div>

              {/* Pass 79 — manual clientNotes are clientId-NATIVE: a commercial customer with NO Auth account can own
                  explicit manual memory as long as a canonical clientId exists. The CTA needs a resolved clientId. */}
              {resolvedClientId ? (
              <div className="mt-4 rounded-2xl border border-slate-200 p-4">
                <p className="text-sm font-medium text-slate-900">Add note</p>
                <textarea
                  className="mt-3 w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-700"
                  rows={4}
                  value={noteForm.noteText}
                  onChange={(event) => setNoteForm((current) => ({ ...current, noteText: event.target.value }))}
                  placeholder="Goals, injuries, equipment notes, personality context, or family details."
                />
                <input
                  className="mt-3 w-full rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-700"
                  value={noteForm.tags}
                  onChange={(event) => setNoteForm((current) => ({ ...current, tags: event.target.value }))}
                  placeholder="Tags: junior, injury, equipment"
                />
                <select
                  className="mt-3 rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-700"
                  value={noteForm.visibility}
                  onChange={(event) => setNoteForm((current) => ({ ...current, visibility: event.target.value }))}
                >
                  <option value="org">Organization team</option>
                  <option value="instructor-private">Instructor private</option>
                </select>
                <button
                  type="button"
                  onClick={handleCreateNote}
                  className="mt-3 rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Save Note
                </button>
              </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                  This client has no canonical commercial identity on record yet, so a manual note cannot be attached.
                </div>
              )}
            </div>
          ) : null}

          {/* Pass 80 §60 — compact next-best-action panel driven by the canonical retention contract. Truthful for
              a no-Auth commercial client: no fabricated package/message/service. */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-950">Next Best Action</h2>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {retentionOpportunity.attentionState === 'needs_attention' ? 'Needs attention'
                  : retentionOpportunity.attentionState === 'worth_reviewing' ? 'Worth reviewing' : 'No action needed'}
              </span>
            </div>
            <div className="mt-4 space-y-2 text-sm text-slate-700">
              <p><span className="font-medium text-slate-900">Recommended:</span> {({
                REBOOK: 'Rebook — the client looks due for a next session',
                FOLLOW_UP: 'Follow up — a session is approaching due',
                USE_EXISTING_CREDIT: 'Use existing credit — prepaid balance remains',
                REVIEW_EXISTING_REQUEST: 'Review the open rebook request',
                NO_ACTION: 'No action needed right now',
              })[retentionOpportunity.nextBestAction] || 'No action needed right now'}</p>
              <p><span className="font-medium text-slate-900">Suggested service:</span> {retentionOpportunity.suggestedService?.name || 'None (operator selects)'}</p>
              <p><span className="font-medium text-slate-900">Last delivered service:</span> {retentionOpportunity.lastDeliveredService?.name || retentionOpportunity.lastDeliveredService?.serviceId || 'Not confirmed delivered'}
                {typeof retentionOpportunity.daysSinceLastDeliveredService === 'number' ? ` · ${retentionOpportunity.daysSinceLastDeliveredService} days ago` : ''}</p>
              <p><span className="font-medium text-slate-900">Package:</span> {retentionOpportunity.packageState?.available
                ? `${retentionOpportunity.packageState.remainingCredits ?? ''} credit(s) available`
                : 'Unavailable (no linked app account)'}</p>
              <p><span className="font-medium text-slate-900">Direct message:</span> {retentionOpportunity.contactCapability?.canDirectMessage ? 'Available' : 'Unavailable (no linked app account)'}</p>
              {(retentionOpportunity.reasons || []).length > 0 ? (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-500">
                  {retentionOpportunity.reasons.slice(0, 4).map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Retention Snapshot</h2>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
              <p><span className="font-medium text-slate-900">Suggested next service:</span> {suggestedService?.name || 'None yet'}</p>
              <p><span className="font-medium text-slate-900">Recent message context:</span> {latestMessageThread?.summary || 'No recent thread yet'}</p>
              <p><span className="font-medium text-slate-900">Quick action:</span> Rebook, message, or assign new coverage based on the latest session.</p>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Retention Action History</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-600">Total Actions</p>
                <p className="mt-2 text-3xl font-bold text-slate-950">{retentionActionSummary.totalCount || 0}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-600">Follow-Ups Sent</p>
                <p className="mt-2 text-3xl font-bold text-slate-950">{retentionActionSummary.followUpSentCount || 0}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-600">Package Offers</p>
                <p className="mt-2 text-3xl font-bold text-slate-950">{retentionActionSummary.packageOfferedCount || 0}</p>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {retentionActions.slice(0, 6).map((action) => (
                <div key={action.retentionActionId || action.id} className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">{action.actionType}</p>
                  <p className="mt-1">{action.note || 'Retention action logged.'}</p>
                  <p className="mt-2 text-xs text-slate-500">{formatDate(action.createdAt)}</p>
                </div>
              ))}
              {retentionActions.length === 0 ? <p className="text-sm text-slate-500">No retention actions logged yet.</p> : null}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ClientProfilePage;
